/* eslint-disable prettier/prettier */
import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Divider,
  Paper,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputAdornment,
  Tabs,
  Tab,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import LockIcon from '@material-ui/icons/Lock';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import { BASE_API, BASE_S3, BUTTON_BACKGROUND_COLOR, BUTTON_TEXT_COLOR, COMPANY_COLORS, USER_TOKEN } from 'config/consts';
import { getActionById } from '../../activities/activitiesActions';
import InputMask from 'react-input-mask';
import { toastr } from 'react-redux-toastr';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  logo: {
    maxWidth: 200,
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    color: COMPANY_COLORS?.primary || '#333',
  },
  productImage: {
    width: '100%',
    maxWidth: 150,
    height: 'auto',
    borderRadius: 8,
  },
  totalPrice: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: COMPANY_COLORS?.primary || '#1976d2',
  },
  payButton: {
    padding: theme.spacing(1),
    fontSize: '0.95rem',
    fontWeight: 'bold',
    marginTop: theme.spacing(2),
  },
  securityInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    color: theme.palette?.text?.secondary || '#666',
  },
  securityIcon: {
    marginRight: theme.spacing(1),
    color: '#4caf50',
  },
  paymentMethodCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    cursor: 'pointer',
    border: '2px solid transparent',
    transition: 'border-color 0.2s',
    '&:hover': {
      borderColor: COMPANY_COLORS?.primary || '#1976d2',
    },
  },
  paymentMethodSelected: {
    borderColor: COMPANY_COLORS?.primary || '#1976d2',
    backgroundColor: '#f0f7ff',
  },
  inputField: {
    marginBottom: theme.spacing(2),
  },
}));

function VendasOnlineParceiroPayment({ activities, auth, getActionById, history }) {
  const classes = useStyles();
  const { id } = useParams();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('pix');
  const [quantity, setQuantity] = useState(1);
  const [infoTabIndex, setInfoTabIndex] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    cpf: '',
    email: '',
    phone: '',
    // Dados do cartão
    cardNumber: '',
    cardName: '',
    cardExpiry: '',
    cardCvv: '',
  });

  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      productIndex: parseInt(params.get('product_index') || '0'),
      initialQuantity: parseInt(params.get('quantity') || '1'),
    };
  }, [location.search]);

  const activity = activities.detail;

  const parsedBody = useMemo(() => {
    try {
      // Preferir dados estruturados vindos do backend em station_specific_data
      if (activity?.station_specific_data) {
        const rawConfig = activity.station_specific_data;
        return typeof rawConfig === 'string' ? JSON.parse(rawConfig) : rawConfig;
      }

      if (activity?.body) {
        return typeof activity.body === 'string' ? JSON.parse(activity.body) : activity.body;
      }
    } catch (e) {
      console.warn('Erro ao parsear configuração da atividade:', e);
    }
    return {};
  }, [activity?.station_specific_data, activity?.body]);

  const rawProducts = activity?.products || parsedBody.products || [];
  const products = useMemo(() => rawProducts.map(p => ({
    id: p.id,
    name: p.name,
    description: p.description,
    price: parseFloat(p.value || p.price) || 0,
    discount: parseFloat(p.discount_value || p.discount) || 0,
    stock: p.stock,
    limit: p.limit || p.limit_per_user,
    image: p.image,
    order: p.order,
  })), [rawProducts]);
  
  const selectedProduct = products[queryParams.productIndex] || products[0];
  const paymentMethods = parsedBody.payment_methods || ['pix', 'credit_card', 'boleto'];
  const allowInstallments = parsedBody.allow_installments || false;
  const installmentsCount = parsedBody.installments_count || 12;
  const recurrenceType = parsedBody.recurrence_type || 'single';

  // Limites
  const activityLimit = activity?.limit || 0; // 0 = sem limite
  const activityLimitPerUser = activity?.limitPerUser || null;
  const productStock = selectedProduct?.stock || null;
  const productLimitPerUser = selectedProduct?.limit || null;

  // Calcular quantidade máxima permitida
  const maxQuantity = useMemo(() => {
    let max = 999;
    if (productStock !== null && productStock > 0) {
      max = Math.min(max, productStock);
    }
    if (productLimitPerUser !== null && productLimitPerUser > 0) {
      max = Math.min(max, productLimitPerUser);
    }
    if (activityLimitPerUser !== null && activityLimitPerUser > 0) {
      max = Math.min(max, activityLimitPerUser);
    }
    return max;
  }, [productStock, productLimitPerUser, activityLimitPerUser]);

  useEffect(() => {
    if (id) {
      getActionById(auth.user.id, id, true);
    }
  }, [id]);

  useEffect(() => {
    if (activity?.id) {
      setLoading(false);
      setFormData(prev => ({
        ...prev,
        name: auth.user?.name || '',
        cpf: auth.user?.cpf || '',
        email: auth.user?.email || '',
        phone: auth.user?.phone || '',
      }));
      setQuantity(Math.min(queryParams.initialQuantity, maxQuantity));
    }
  }, [activity, auth.user, queryParams.initialQuantity, maxQuantity]);

  const formatCurrency = (value) => {
    return `R$ ${(value || 0).toFixed(2).replace('.', ',')}`;
  };

  const getDiscountedPrice = (product) => {
    if (product?.discount && product.discount > 0) {
      return product.price - product.discount;
    }
    return product?.price || 0;
  };

  const totalPrice = useMemo(() => {
    if (!selectedProduct) return 0;
    return getDiscountedPrice(selectedProduct) * quantity;
  }, [selectedProduct, quantity]);

  const handleInputChange = (field) => (e) => {
    let value = '';

    if (e && typeof e === 'object' && 'target' in e && e.target) {
      value = e.target.value;
    } else if (e && typeof e === 'string') {
      value = e;
    } else if (e && typeof e.value !== 'undefined') {
      value = e.value;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    setQuantity(Math.max(1, Math.min(value, maxQuantity)));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (!activity || !selectedProduct) {
        toastr.error('Ops!', 'Não foi possível identificar o produto ou a atividade. Tente novamente.');
        return;
      }

      // Validar dados básicos do cliente
      if (!formData.email || !formData.phone) {
        toastr.warning('Atenção', 'Informe um e-mail e telefone para continuar.');
        return;
      }

      const paymentMethodsForLink = (paymentMethods || []).map((m) => {
        if (m === 'credit_card') return 'cartao';
        return m;
      });

      // Dados do parceiro / split (herdados do body da atividade)
      const partnersSplit = parsedBody.partners_split || [];
      const mainPartner = partnersSplit[0] || null;
      const hasPartner = !!(parsedBody.has_partner || mainPartner);

      let partnerPercentage = 0;
      if (hasPartner && mainPartner?.percentage !== undefined && mainPartner?.percentage !== null) {
        partnerPercentage = parseFloat(mainPartner.percentage) || 0;
      }
      partnerPercentage = Math.max(0, Math.min(100, partnerPercentage));
      const stationPercentage = hasPartner ? Math.max(0, Math.min(100, 100 - partnerPercentage)) : 100;

      const payload = {
        // Dados do cliente
        customer_name: formData.name,
        customer_cpf: formData.cpf,
        customer_email: formData.email,
        customer_phone: formData.phone,
        service_name: activity?.name || parsedBody.name || selectedProduct.name,
        link_value: totalPrice,
        payment_methods: paymentMethodsForLink,
        allow_installments: !!allowInstallments,
        // Split com parceiro
        has_partner: hasPartner,
        station_percentage: stationPercentage,
        partner_percentage: partnerPercentage,
        partner_recipient_id: hasPartner ? mainPartner?.recipient_id || null : null,
        partner_user_id: hasPartner ? mainPartner?.user_id || null : null,
        partner_name: hasPartner ? mainPartner?.name || null : null,
        // IDs
        station_id: auth.user?.station_id || auth.user?.station?.id || activity.station_id,
        establishment_id: activity.station?.establishment_id || null,
        activity_id: activity.id,
        // Produto/quantidade comprados (usado para criar os vouchers após confirmação do pagamento)
        product_id: selectedProduct.id,
        quantity,
        // Recorrência
        recurrence_type: recurrenceType,
      };

      const token = JSON.parse(localStorage.getItem(USER_TOKEN));

      const response = await axios.post(`${BASE_API}/payment-links/generate`, payload, {
        headers: {
          Authorization: token,
        },
      });

      if (response.data?.success && response.data?.payment_link?.checkout_url) {
        // Redirecionar para página pública de pagamento (PagamentoPage),
        // que já exibe QRCode PIX, código copia-e-cola e fluxo de cartão/boleto
        const checkoutUrl = response.data.payment_link.checkout_url;
        window.location.href = checkoutUrl;
      } else {
        console.error('Erro ao gerar link de pagamento:', response.data);
        toastr.error('Erro', response.data?.message || 'Erro ao gerar link de pagamento. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao processar pagamento:', error);
      const message = error.response?.data?.message || error.message || 'Erro ao processar pagamento. Tente novamente.';
      toastr.error('Erro', message);
    } finally {
      setSubmitting(false);
    }
  };

  // Conteúdos de descrição/regulamento/termos vindos da atividade
  const descriptionHtml = parsedBody.description || activity?.description || '';
  const rulesHtml = parsedBody.rules || activity?.rules || '';
  const termsHtml = parsedBody.terms || '';

  const hasDescription = !!(descriptionHtml && descriptionHtml.trim());
  const hasRules = !!(rulesHtml && rulesHtml.trim());
  const hasTerms = !!(termsHtml && termsHtml.trim());

  const infoTabs = [];
  if (hasDescription) {
    infoTabs.push({ key: 'description', label: 'Geral', content: descriptionHtml });
  }
  if (hasRules) {
    infoTabs.push({ key: 'rules', label: 'Regulamento', content: rulesHtml });
  }
  if (hasTerms) {
    infoTabs.push({ key: 'terms', label: 'Termos e condições', content: termsHtml });
  }

  const renderHtmlContent = (html) => {
    if (!html) return null;
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Grid container spacing={3} justifyContent="center">
        {/* Coluna Principal */}
        <Grid item xs={12} md={8}>
          {/* Logo e Informações da Estação */}
          <Paper className={classes.paper}>
            <Box display="flex" flexDirection="column" alignItems="center">
              {activity?.station?.image && (
                <img
                  src={`${BASE_S3}/${activity.station.image}`}
                  alt="Logo"
                  className={classes.logo}
                />
              )}
              <Typography variant="h5" align="center" gutterBottom>
                {activity?.name}
              </Typography>
              {activity?.station?.company_name && (
                <Typography variant="body2" color="textSecondary">
                  {activity.station.company_name}
                </Typography>
              )}
              {infoTabs.length > 0 && (
                <Box mt={2} width="100%">
                  {infoTabs.length === 1 ? (
                    renderHtmlContent(infoTabs[0].content)
                  ) : (
                    <>
                      <Tabs
                        value={infoTabIndex}
                        onChange={(e, newValue) => setInfoTabIndex(newValue)}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="scrollable"
                        scrollButtons="auto">
                        {infoTabs.map((tab) => (
                          <Tab key={tab.key} label={tab.label} />
                        ))}
                      </Tabs>
                      <Box mt={2}>{renderHtmlContent(infoTabs[infoTabIndex].content)}</Box>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Paper>

          {/* Dados do Cliente */}
          <Paper className={classes.paper}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Dados do Cliente
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="CPF"
                  value={formData.cpf}
                  onChange={handleInputChange('cpf')}
                  fullWidth
                  variant="outlined"
                  className={classes.inputField}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nome Completo"
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  fullWidth
                  variant="outlined"
                  className={classes.inputField}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="E-mail"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  fullWidth
                  variant="outlined"
                  className={classes.inputField}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputMask
                  mask="(99) 99999-9999"
                  value={formData.phone}
                  onChange={handleInputChange('phone')}
                >
                  {(inputProps) => (
                    <TextField
                      {...inputProps}
                      label="Telefone/WhatsApp"
                      fullWidth
                      variant="outlined"
                      className={classes.inputField}
                    />
                  )}
                </InputMask>
              </Grid>
            </Grid>
          </Paper>

          {/* Resumo do Pedido */}
          <Paper className={classes.paper}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Confirme os itens de sua compra
            </Typography>
            
            {selectedProduct && (
              <Box display="flex" alignItems="flex-start" mb={2}>
                {selectedProduct.image && (
                  <img
                    src={`${BASE_S3}/${selectedProduct.image}`}
                    alt={selectedProduct.name}
                    className={classes.productImage}
                    style={{ marginRight: 16 }}
                  />
                )}
                <Box flex={1}>
                  <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                    {selectedProduct.name}
                  </Typography>
                  {selectedProduct.description && (
                    <Typography variant="body2" color="textSecondary">
                      {selectedProduct.description}
                    </Typography>
                  )}
                  <Typography variant="body2" style={{ marginTop: 8 }}>
                    Preço unitário: {formatCurrency(getDiscountedPrice(selectedProduct))}
                    {selectedProduct.discount > 0 && (
                      <span style={{ textDecoration: 'line-through', marginLeft: 8, color: '#999' }}>
                        {formatCurrency(selectedProduct.price)}
                      </span>
                    )}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Seleção de Quantidade */}
            <Box mb={2}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <TextField
                    type="number"
                    label="Quantidade"
                    value={quantity}
                    onChange={handleQuantityChange}
                    inputProps={{ min: 1, max: maxQuantity }}
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    {maxQuantity < 999 ? `Máximo: ${maxQuantity} unidade(s)` : 'Sem limite'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider style={{ margin: '16px 0' }} />

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Total</Typography>
              <Typography className={classes.totalPrice}>
                {formatCurrency(totalPrice)}
              </Typography>
            </Box>

            {allowInstallments && (
              <Typography variant="body2" color="textSecondary" align="right">
                ou em até {installmentsCount}x de {formatCurrency(totalPrice / installmentsCount)}
              </Typography>
            )}
          </Paper>

          {/* Botão de Pagamento */}
          <Button
            variant="contained"
            fullWidth
            className={classes.payButton}
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              backgroundColor: BUTTON_BACKGROUND_COLOR,
              color: BUTTON_TEXT_COLOR,
            }}
          >
            {submitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              `Pagar agora - ${formatCurrency(totalPrice)}`
            )}
          </Button>

          {/* Informação de Segurança */}
          <Box className={classes.securityInfo}>
            <LockIcon className={classes.securityIcon} fontSize="small" />
            <Typography variant="caption">
              Ambiente criptografado e 100% seguro - SSL Certificado
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  activities: state.activities,
  auth: state.auth,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ getActionById }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(VendasOnlineParceiroPayment);
